import React, { useState, useEffect } from "react";
import {
  Button,
  message,
  Row,
  Col,
  Modal,
  Input,
  DatePicker,
  Select,
  Spin,
  Tooltip,
  InputNumber,
  Divider,
} from "antd";
import {
  QuestionCircleOutlined,
  CheckOutlined,
  CloseOutlined,
  CloudDownloadOutlined,
  FilePdfOutlined,
  AimOutlined,
} from "@ant-design/icons";
import { useHistory } from "react-router-dom";
import { CSVLink } from "react-csv";
import moment from "moment";
import { useFetchConstant } from "apis/constant";
import { useGetUsers, patchUpdateUser, refetchUsers } from "apis/user";
import { getGoalReport, useGetGoalReport } from "apis/goal";

import Title from "components/Title/Title";

import { withAppContext } from "contexts/AppContext/AppContext";
import { getLabelOfConstant } from "utils/general";
import { buildUserNewUri, buildUserUri, buildProfileUri } from "utils/routes";
import {
  getColumnFilterSearchProps,
  constructColumn,
  handleOnAggregationTableChange,
} from "utils/table/table";
import { UsersTable } from "./FinportGoals.styles";
import { getAllProducts } from "apis/product";
import { assignCutOffDate } from "apis/billing";
import { PrimaryColor, ScreenSize } from "utils/constants";
import Locale from "react-intl-universal";

const FinportGoals = ({ getLoggedInUser, isAdmin }) => {
  const history = useHistory();
  const [freeTrialLoading, setFreeTrialLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [query, setQuery] = useState({ currentPage: 1, limit: null });
  const loggedInUser = getLoggedInUser();
  const [loading, setLoading] = useState(false);
  const [goalReportPDF, setGoalReportPDF] = useState(null);
  const [adviseeName, setAdviseeName] = useState("");
  const [targetNetworth, setTargetNetworth] = useState(0);

  const [cashEquivalent, setCashEquivalent] = useState(0);
  const [totalPropertyWorth, setTotalPropertyWorth] = useState(0);
  const [totalBusinessWorth, setTotalBusinessWorth] = useState(0);
  const [others, setOthers] = useState(0);

  const [totalMortgageBalance, setTotalMortgageBalance] = useState(0);
  const [carLoanBalance, setCarLoanBalance] = useState(0);
  const [
    creditCardPersonalLoanBalance,
    setCreditCardPersonalLoanBalance,
  ] = useState(0);

  const [monthlyTotalInflow, setMonthlyTotalInflow] = useState(0);
  const [monthlyActiveIncome, setMonthlyActiveIncome] = useState(0);
  const [monthlyPassiveIncome, setMonthlyPassiveIncome] = useState(0);
  const [monthlyTotalOutflow, setMonthlyTotalOutflow] = useState(0);
  const [
    monthlyMortgageLoanRepayment,
    setMonthlyMortgageLoanRepayment,
  ] = useState(0);
  const [
    monthlyNonMortgageLoanPayment,
    setMonthlyNonMortgageLoanPayment,
  ] = useState(0);
  const [
    monthlyTotalOutflowMinSpend,
    setMonthlyTotalOutflowMinSpend,
  ] = useState(0);
  const [insurancePremiumPerMonth, setInsurancePremiumPerMonth] = useState(0);
  const [totalLifeInsuranceCoverage, setTotalLifeInsuranceCoverage] = useState(
    0
  );

  const { isLoading, paginatedData: users, total } = useGetGoalReport(query);

  // useEffect(() => {
  //   if (users) {
  //     getUsersSubscriptionStatus();
  //   }
  // }, [users]);

  const {
    isLoading: isAccountStatusesLoading,
    selection: accountStatuses,
    data: accountStatusesConst,
  } = useFetchConstant("accountStatuses");

  const loadGoalReportPDF = async (goalReportId) => {
    setLoading(true);
    const response = await getGoalReport(goalReportId);
    setGoalReportPDF(response);
    setLoading(false);
  };

  const columns = [
    {
      ...constructColumn(
        Locale.get("finportGoal.finportGoalComponent.dos"),
        "createdAt",
        { hasSorter: true }
      ),
      render: (createdAt) => moment(createdAt).format("DD/MM/YYYY"),
    },
    {
      ...constructColumn(
        Locale.get("finportGoal.finportGoalComponent.fullName"),
        "name",
        { hasSorter: true }
      ),
      ...getColumnFilterSearchProps("name", "Search name"),
    },
    {
      ...constructColumn(
        Locale.get("finportGoal.finportGoalComponent.email"),
        "email"
      ),
      ...getColumnFilterSearchProps("email", "Search Email"),
    },
    {
      ...constructColumn(
        Locale.get("finportGoal.finportGoalComponent.pdf"),
        null
      ),
      render: (value) => (
        <Button
          type="link"
          icon={<FilePdfOutlined />}
          style={{
            color: PrimaryColor,
          }}
          onClick={() => {
            loadGoalReportPDF(value._id);
            setAdviseeName(value.reportObject.adviseeName);
            setTargetNetworth(value.reportObject.targetNetworth);
            setCashEquivalent(value.reportObject.cashEquivalent);
            setTotalPropertyWorth(value.reportObject.totalPropertyWorth);
            setTotalBusinessWorth(value.reportObject.totalBusinessWorth);
            setOthers(value.reportObject.others);
            setTotalMortgageBalance(value.reportObject.totalMortgageBalance);
            setCarLoanBalance(value.reportObject.carLoanBalance);
            setCreditCardPersonalLoanBalance(
              value.reportObject.creditCardPersonalLoanBalance
            );
            setMonthlyTotalInflow(value.reportObject.monthlyTotalInflow);
            setMonthlyActiveIncome(value.reportObject.monthlyActiveIncome);
            setMonthlyPassiveIncome(value.reportObject.monthlyPassiveIncome);
            setMonthlyTotalOutflow(value.reportObject.monthlyTotalOutflow);
            setMonthlyTotalOutflowMinSpend(
              value.reportObject.monthlyTotalOutflowMinSpend
            );
            setMonthlyMortgageLoanRepayment(
              value.reportObject.monthlyMortgageLoanRepayment
            );
            setMonthlyNonMortgageLoanPayment(
              value.reportObject.monthlyNonMortgageLoanPayment
            );
            setInsurancePremiumPerMonth(
              value.reportObject.insurancePremiumPerMonth
            );
            setTotalLifeInsuranceCoverage(
              value.reportObject.totalLifeInsuranceCoverage
            );
            setVisible(true);
          }}
        >
          {Locale.get("finportGoal.finportGoalComponent.pdf")}
        </Button>
      ),
    },
    // {
    //   ...constructColumn("Status", "status", { hasSorter: true }),
    //   render: (status) => getLabelOfConstant(status, accountStatuses),
    // },
  ];

  // Export CSV

  const [csv_data, set_csv_data] = useState([]);
  const [csv_header, set_csv_header] = useState([
    {
      label: "Creation Date",
      key: "createdAt",
    },
    {
      label: "Name",
      key: "name",
    },
    {
      label: "Email",
      key: "email",
    },
  ]);

  const generateCSV = async () => {
    const now = Math.floor(Date.now() / 1000);
    var order = [...users];
    var csv_data = [];

    console.log("order", order);

    for (let i = 0; i < order.length; i++) {
      var user = order[i];

      csv_data.push({
        createdAt: moment(user.createdAt).format("DD-MMM-YYYY"),
        name: user.name,
        email: user.email,
      });
    }

    set_csv_data(csv_data);

    return true;
  };

  const ResetAllFields = () => {
    setAdviseeName("");
    setTargetNetworth("");
    setCashEquivalent("");
    setTotalPropertyWorth("");
    setTotalBusinessWorth("");
    setOthers("");
    setTotalMortgageBalance("");
    setCarLoanBalance("");
    setCreditCardPersonalLoanBalance("");
    setMonthlyTotalInflow("");
    setMonthlyActiveIncome("");
    setMonthlyPassiveIncome("");
    setMonthlyTotalOutflow("");
    setMonthlyTotalOutflowMinSpend("");
    setMonthlyMortgageLoanRepayment("");
    setMonthlyNonMortgageLoanPayment("");
    setInsurancePremiumPerMonth("");
    setTotalLifeInsuranceCoverage("");
    setGoalReportPDF(null);
  };

  const PDFModal = (
    <Modal
      visible={visible}
      title={Locale.get("finportGoal.pdf.pdfPreview")}
      width={window.innerWidth < ScreenSize.xl ? "90%" : "60%"}
      onCancel={() => {
        setVisible(false);
        ResetAllFields();
      }}
      onOk={() => {
        setVisible(false);
        ResetAllFields();
      }}
      okButtonProps={{ loading: freeTrialLoading }}
      cancelButtonProps={{
        disabled: freeTrialLoading,
        style: { display: "none" },
      }}
    >
      <Row align="middle" justify="space-between" gutter={16}>
        <Col
          xs={24}
          md={24}
          xl={10}
          style={{
            overflowY: window.innerWidth < ScreenSize.xl ? "hidden" : "scroll",
            overflowX: "hidden",
            maxHeight: window.innerWidth < ScreenSize.xl ? "100%" : 606,
            paddingRight: window.innerWidth < ScreenSize.xl ? 0 : 16,
            marginBottom: window.innerWidth < ScreenSize.xl && 24,
          }}
        >
          <Row
            style={{
              fontSize: window.innerWidth < ScreenSize.md ? 8 : 16,
              fontWeight: "bold",
              marginBottom: 8,
            }}
          >
            {Locale.get("finportGoal.pdf.financeRatioInput")}
          </Row>

          <Row>
            <Col xs={24} md={24} xl={24} style={{ marginBottom: 16 }}>
              <Row gutter={8} style={{ marginBottom: 8 }}>
                <Col>{Locale.get("finportGoal.pdf.name")}</Col>
              </Row>
              <Row>
                <Input
                  value={adviseeName}
                  readOnly
                  style={{ width: "100%", height: 35, fontSize: 18 }}
                />
              </Row>
            </Col>
          </Row>

          <Row>
            <Col xs={24} md={24} xl={24}>
              <Row gutter={8} style={{ marginBottom: 8 }}>
                <Col>
                  <Tooltip
                    title={Locale.get("finportGoal.pdf.toolTipNetworth")}
                    overlayInnerStyle={{ fontSize: 16, fontWeight: 500 }}
                  >
                    <QuestionCircleOutlined
                      style={{ color: "rgba(0,0,0,0.45)" }}
                    />
                  </Tooltip>
                </Col>

                <Col>{Locale.get("finportGoal.pdf.tNetworth")}</Col>
              </Row>
              <Row>
                <InputNumber
                  min={0}
                  value={targetNetworth}
                  readOnly
                  style={{ width: "100%", height: 35, fontSize: 18 }}
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(value) =>
                    Number(value.replace(/(,*)/g, "")).toFixed(2)
                  }
                />
              </Row>
            </Col>
          </Row>

          <Divider style={{ backgroundColor: "rgba(0,0,0,0.15)" }} />

          <Row style={{ fontSize: 24, marginBottom: 8 }}>Assets</Row>

          <Row>
            <Col xs={24} md={24} xl={24} style={{ marginBottom: 16 }}>
              <Row gutter={8} style={{ marginBottom: 8 }}>
                <Col>
                  <Tooltip
                    title={Locale.get("finportGoal.pdf.toolTipCashAndCash")}
                    overlayInnerStyle={{ fontSize: 16, fontWeight: 500 }}
                  >
                    <QuestionCircleOutlined
                      style={{ color: "rgba(0,0,0,0.45)" }}
                    />
                  </Tooltip>
                </Col>

                <Col>{Locale.get("finportGoal.pdf.cashAndCash")}</Col>
              </Row>

              <Row>
                <InputNumber
                  min={0}
                  value={cashEquivalent}
                  style={{ width: "100%", height: 35, fontSize: 18 }}
                  readOnly
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(value) =>
                    Number(value.replace(/(,*)/g, "")).toFixed(2)
                  }
                />
              </Row>
            </Col>
          </Row>

          <Row>
            <Col xs={24} md={24} xl={24} style={{ marginBottom: 16 }}>
              <Row gutter={8} style={{ marginBottom: 8 }}>
                <Col>
                  <Tooltip
                    title={Locale.get(
                      "finportGoal.pdf.toolTipTotalPropertyWorth"
                    )}
                    overlayInnerStyle={{ fontSize: 16, fontWeight: 500 }}
                  >
                    <QuestionCircleOutlined
                      style={{ color: "rgba(0,0,0,0.45)" }}
                    />
                  </Tooltip>
                </Col>

                <Col>{Locale.get("finportGoal.pdf.totalPropertyWorth")}</Col>
              </Row>

              <Row>
                <InputNumber
                  min={0}
                  value={totalPropertyWorth}
                  style={{ width: "100%", height: 35, fontSize: 18 }}
                  readOnly
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(value) =>
                    Number(value.replace(/(,*)/g, "")).toFixed(2)
                  }
                />
              </Row>
            </Col>
          </Row>

          <Row>
            <Col xs={24} md={24} xl={24} style={{ marginBottom: 16 }}>
              <Row gutter={8} style={{ marginBottom: 8 }}>
                <Col>
                  <Tooltip
                    title={Locale.get(
                      "finportGoal.pdf.toolTipTotalBusinessWorth"
                    )}
                    overlayInnerStyle={{ fontSize: 16, fontWeight: 500 }}
                  >
                    <QuestionCircleOutlined
                      style={{ color: "rgba(0,0,0,0.45)" }}
                    />
                  </Tooltip>
                </Col>

                <Col>{Locale.get("finportGoal.pdf.totalBusinessWorth")}</Col>
              </Row>

              <Row>
                <InputNumber
                  min={0}
                  value={totalBusinessWorth}
                  style={{ width: "100%", height: 35, fontSize: 18 }}
                  readOnly
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(value) =>
                    Number(value.replace(/(,*)/g, "")).toFixed(2)
                  }
                />
              </Row>
            </Col>
          </Row>

          <Row>
            <Col xs={24} md={24} xl={24}>
              <Row gutter={8} style={{ marginBottom: 8 }}>
                <Col>
                  <Tooltip
                    title={Locale.get("finportGoal.pdf.toolTipOther")}
                    overlayInnerStyle={{ fontSize: 16, fontWeight: 500 }}
                  >
                    <QuestionCircleOutlined
                      style={{ color: "rgba(0,0,0,0.45)" }}
                    />
                  </Tooltip>
                </Col>

                <Col>{Locale.get("finportGoal.pdf.other")}</Col>
              </Row>

              <Row>
                <InputNumber
                  min={0}
                  value={others}
                  style={{ width: "100%", height: 35, fontSize: 18 }}
                  readOnly
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(value) =>
                    Number(value.replace(/(,*)/g, "")).toFixed(2)
                  }
                />
              </Row>
            </Col>
          </Row>

          <Divider style={{ backgroundColor: "rgba(0,0,0,0.15)" }} />

          <Row style={{ fontSize: 24, marginBottom: 8 }}>
            {Locale.get("finportGoal.pdf.liabilities")}
          </Row>

          <Row>
            <Col xs={24} md={24} xl={24} style={{ marginBottom: 16 }}>
              <Row gutter={8} style={{ marginBottom: 8 }}>
                <Col>
                  <Tooltip
                    title={Locale.get(
                      "finportGoal.pdf.toolTipTotalMortgageBalance"
                    )}
                    overlayInnerStyle={{ fontSize: 16, fontWeight: 500 }}
                  >
                    <QuestionCircleOutlined
                      style={{ color: "rgba(0,0,0,0.45)" }}
                    />
                  </Tooltip>
                </Col>

                <Col>{Locale.get("finportGoal.pdf.tMortgageBalance")}</Col>
              </Row>

              <Row>
                <InputNumber
                  min={0}
                  value={totalMortgageBalance}
                  style={{ width: "100%", height: 35, fontSize: 18 }}
                  readOnly
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(value) =>
                    Number(value.replace(/(,*)/g, "")).toFixed(2)
                  }
                />
              </Row>
            </Col>
          </Row>

          <Row>
            <Col xs={24} md={24} xl={24} style={{ marginBottom: 16 }}>
              <Row gutter={8} style={{ marginBottom: 8 }}>
                <Col>
                  <Tooltip
                    title={Locale.get("finportGoal.pdf.toolTipCarLoanBalance")}
                    overlayInnerStyle={{ fontSize: 16, fontWeight: 500 }}
                  >
                    <QuestionCircleOutlined
                      style={{ color: "rgba(0,0,0,0.45)" }}
                    />
                  </Tooltip>
                </Col>

                <Col>{Locale.get("finportGoal.pdf.carLoanBalance")}</Col>
              </Row>

              <Row>
                <InputNumber
                  min={0}
                  value={carLoanBalance}
                  style={{ width: "100%", height: 35, fontSize: 18 }}
                  readOnly
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(value) =>
                    Number(value.replace(/(,*)/g, "")).toFixed(2)
                  }
                />
              </Row>
            </Col>
          </Row>

          <Row>
            <Col xs={24} md={24} xl={24}>
              <Row gutter={8} style={{ marginBottom: 8 }}>
                <Col>
                  <Tooltip
                    title={Locale.get(
                      "finportGoal.pdf.toolTipCreditCardPersonalLoanBalance"
                    )}
                    overlayInnerStyle={{ fontSize: 16, fontWeight: 500 }}
                  >
                    <QuestionCircleOutlined
                      style={{ color: "rgba(0,0,0,0.45)" }}
                    />
                  </Tooltip>
                </Col>

                <Col>
                  {Locale.get("finportGoal.pdf.creditCardPersonalLoanBalance")}
                </Col>
              </Row>

              <Row>
                <InputNumber
                  min={0}
                  value={creditCardPersonalLoanBalance}
                  style={{ width: "100%", height: 35, fontSize: 18 }}
                  readOnly
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(value) =>
                    Number(value.replace(/(,*)/g, "")).toFixed(2)
                  }
                />
              </Row>
            </Col>
          </Row>

          <Divider style={{ backgroundColor: "rgba(0,0,0,0.15)" }} />

          <Row style={{ fontSize: 24, marginBottom: 8 }}>
            {Locale.get("finportGoal.pdf.cashFlow")}
          </Row>

          <Row>
            <Col xs={24} md={24} xl={24} style={{ marginBottom: 16 }}>
              <Row gutter={8} style={{ marginBottom: 8 }}>
                <Col>
                  <Tooltip
                    title={Locale.get(
                      "finportGoal.pdf.toolTipMonthlyTotalInflow"
                    )}
                    overlayInnerStyle={{ fontSize: 16, fontWeight: 500 }}
                  >
                    <QuestionCircleOutlined
                      style={{ color: "rgba(0,0,0,0.45)" }}
                    />
                  </Tooltip>
                </Col>

                <Col>{Locale.get("finportGoal.pdf.monthlyTotalInflow")}</Col>
              </Row>

              <Row>
                <InputNumber
                  min={0}
                  value={monthlyTotalInflow}
                  style={{ width: "100%", height: 35, fontSize: 18 }}
                  readOnly
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(value) =>
                    Number(value.replace(/(,*)/g, "")).toFixed(2)
                  }
                />
              </Row>
            </Col>
          </Row>

          <Row>
            <Col xs={24} md={24} xl={24} style={{ marginBottom: 16 }}>
              <Row gutter={8} style={{ marginBottom: 8 }}>
                <Col>
                  <Tooltip
                    title={Locale.get(
                      "finportGoal.pdf.toolTipMonthlyActiveIncome"
                    )}
                    overlayInnerStyle={{ fontSize: 16, fontWeight: 500 }}
                  >
                    <QuestionCircleOutlined
                      style={{ color: "rgba(0,0,0,0.45)" }}
                    />
                  </Tooltip>
                </Col>

                <Col>{Locale.get("finportGoal.pdf.monthlyActiveIncome")}</Col>
              </Row>

              <Row>
                <InputNumber
                  min={0}
                  value={monthlyActiveIncome}
                  style={{ width: "100%", height: 35, fontSize: 18 }}
                  readOnly
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(value) =>
                    Number(value.replace(/(,*)/g, "")).toFixed(2)
                  }
                />
              </Row>
            </Col>
          </Row>

          <Row>
            <Col xs={24} md={24} xl={24} style={{ marginBottom: 16 }}>
              <Row gutter={8} style={{ marginBottom: 8 }}>
                <Col>
                  <Tooltip
                    title={Locale.get(
                      "finportGoal.pdf.toolTipMonthlyPassiveIncome"
                    )}
                    overlayInnerStyle={{ fontSize: 16, fontWeight: 500 }}
                  >
                    <QuestionCircleOutlined
                      style={{ color: "rgba(0,0,0,0.45)" }}
                    />
                  </Tooltip>
                </Col>

                <Col>{Locale.get("finportGoal.pdf.monthlyPassiveIncome")}</Col>
              </Row>

              <Row>
                <InputNumber
                  min={0}
                  value={monthlyPassiveIncome}
                  style={{ width: "100%", height: 35, fontSize: 18 }}
                  readOnly
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(value) =>
                    Number(value.replace(/(,*)/g, "")).toFixed(2)
                  }
                />
              </Row>
            </Col>
          </Row>

          <Row>
            <Col xs={24} md={24} xl={24} style={{ marginBottom: 16 }}>
              <Row gutter={8} style={{ marginBottom: 8 }}>
                <Col>
                  <Tooltip
                    title={Locale.get(
                      "finportGoal.pdf.toolTipMonthlyTotalOutflowNormal"
                    )}
                    overlayInnerStyle={{ fontSize: 16, fontWeight: 500 }}
                  >
                    <QuestionCircleOutlined
                      style={{ color: "rgba(0,0,0,0.45)" }}
                    />
                  </Tooltip>
                </Col>

                <Col>
                  {Locale.get("finportGoal.pdf.monthlyTotalOutflowNormal")}
                </Col>
              </Row>

              <Row>
                <InputNumber
                  min={0}
                  value={monthlyTotalOutflow}
                  style={{ width: "100%", height: 35, fontSize: 18 }}
                  readOnly
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(value) =>
                    Number(value.replace(/(,*)/g, "")).toFixed(2)
                  }
                />
              </Row>
            </Col>
          </Row>

          <Row>
            <Col xs={24} md={24} xl={24} style={{ marginBottom: 16 }}>
              <Row gutter={8} style={{ marginBottom: 8 }}>
                <Col>
                  <Tooltip
                    title={Locale.get(
                      "finportGoal.pdf.toolTipMonthlyTotalOutflowMin"
                    )}
                    overlayInnerStyle={{ fontSize: 16, fontWeight: 500 }}
                  >
                    <QuestionCircleOutlined
                      style={{ color: "rgba(0,0,0,0.45)" }}
                    />
                  </Tooltip>
                </Col>

                <Col>
                  {Locale.get("finportGoal.pdf.monthlyTotalOutflowMin")}
                </Col>
              </Row>

              <Row>
                <InputNumber
                  min={0}
                  value={monthlyTotalOutflowMinSpend}
                  style={{ width: "100%", height: 35, fontSize: 18 }}
                  readOnly
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(value) =>
                    Number(value.replace(/(,*)/g, "")).toFixed(2)
                  }
                />
              </Row>
            </Col>
          </Row>

          <Row>
            <Col xs={24} md={24} xl={24} style={{ marginBottom: 16 }}>
              <Row gutter={8} style={{ marginBottom: 8 }}>
                <Col>
                  <Tooltip
                    title={Locale.get(
                      "finportGoal.pdf.toolTipMonthlyMortgageLoanRepayment"
                    )}
                    overlayInnerStyle={{ fontSize: 16, fontWeight: 500 }}
                  >
                    <QuestionCircleOutlined
                      style={{ color: "rgba(0,0,0,0.45)" }}
                    />
                  </Tooltip>
                </Col>

                <Col>
                  {Locale.get("finportGoal.pdf.monthlyMortgageLoanRepayment")}
                </Col>
              </Row>

              <Row>
                <InputNumber
                  min={0}
                  value={monthlyMortgageLoanRepayment}
                  style={{ width: "100%", height: 35, fontSize: 18 }}
                  readOnly
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(value) =>
                    Number(value.replace(/(,*)/g, "")).toFixed(2)
                  }
                />
              </Row>
            </Col>
          </Row>

          <Row>
            <Col xs={24} md={24} xl={24} style={{ marginBottom: 16 }}>
              <Row gutter={8} style={{ marginBottom: 8 }}>
                <Col>
                  <Tooltip
                    title={Locale.get(
                      "finportGoal.pdf.toolTipMonthlyNonMortgageLoanRepayment"
                    )}
                    overlayInnerStyle={{ fontSize: 16, fontWeight: 500 }}
                  >
                    <QuestionCircleOutlined
                      style={{ color: "rgba(0,0,0,0.45)" }}
                    />
                  </Tooltip>
                </Col>

                <Col>
                  {Locale.get(
                    "finportGoal.pdf.monthlyNonMortgageLoanRepayment"
                  )}
                </Col>
              </Row>

              <Row>
                <InputNumber
                  min={0}
                  value={monthlyNonMortgageLoanPayment}
                  style={{ width: "100%", height: 35, fontSize: 18 }}
                  readOnly
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(value) =>
                    Number(value.replace(/(,*)/g, "")).toFixed(2)
                  }
                />
              </Row>
            </Col>
          </Row>

          <Row>
            <Col xs={24} md={24} xl={24} style={{ marginBottom: 16 }}>
              <Row gutter={8} style={{ marginBottom: 8 }}>
                <Col>
                  <Tooltip
                    title={Locale.get(
                      "finportGoal.pdf.toolTipInsurancePremium"
                    )}
                    overlayInnerStyle={{ fontSize: 16, fontWeight: 500 }}
                  >
                    <QuestionCircleOutlined
                      style={{ color: "rgba(0,0,0,0.45)" }}
                    />
                  </Tooltip>
                </Col>

                <Col>{Locale.get("finportGoal.pdf.insurancePremium")}</Col>
              </Row>

              <Row>
                <InputNumber
                  min={0}
                  value={insurancePremiumPerMonth}
                  style={{ width: "100%", height: 35, fontSize: 18 }}
                  readOnly
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(value) =>
                    Number(value.replace(/(,*)/g, "")).toFixed(2)
                  }
                />
              </Row>
            </Col>
          </Row>

          <Row>
            <Col xs={24} md={24} xl={24}>
              <Row gutter={8} style={{ marginBottom: 8 }}>
                <Col>
                  <Tooltip
                    title={Locale.get(
                      "finportGoal.pdf.toolTipTotalLifeInsuranceCoverage"
                    )}
                    overlayInnerStyle={{ fontSize: 16, fontWeight: 500 }}
                  >
                    <QuestionCircleOutlined
                      style={{ color: "rgba(0,0,0,0.45)" }}
                    />
                  </Tooltip>
                </Col>

                <Col>
                  {Locale.get("finportGoal.pdf.totalLifeInsuranceCoverage")}
                </Col>
              </Row>

              <Row>
                <InputNumber
                  min={0}
                  value={totalLifeInsuranceCoverage}
                  style={{ width: "100%", height: 35, fontSize: 18 }}
                  readOnly
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(value) =>
                    Number(value.replace(/(,*)/g, "")).toFixed(2)
                  }
                />
              </Row>
            </Col>
          </Row>
        </Col>
        <Col xs={24} md={24} xl={14}>
          <div>
            <Spin spinning={loading}>
              <iframe
                src={goalReportPDF}
                style={{
                  width: "100%",
                  height:
                    window.innerHeight < ScreenSize.md
                      ? window.innerHeight * 0.8
                      : window.innerHeight * 0.6,
                }}
              />
            </Spin>
          </div>
        </Col>
      </Row>
    </Modal>
  );

  return (
    <div>
      <Row justify="space-between" align="middle">
        <Col>
          <Title>{Locale.get("finportGoal.pageTitle")}</Title>
        </Col>

        <Col>
          <Row gutter={24}>
            <Col>
              <CSVLink
                data={csv_data}
                headers={csv_header}
                filename={`${moment().format("DD-MMM-YYYY")}_Finport_Goals.csv`}
                onClick={() => generateCSV()}
              >
                <Button
                  icon={<CloudDownloadOutlined style={{ color: "#d7a301" }} />}
                  style={{ color: "#d7a301", borderColor: "#d7a301" }}
                >
                  {Locale.get("finportGoal.csv")}
                </Button>
              </CSVLink>
            </Col>
            <Col>
              <Button
                type="primary"
                icon={<AimOutlined />}
                onClick={() => window.open("https://www.finportgoal.com/")}
              >
                {Locale.get("finportGoal.afg")}
              </Button>
            </Col>
          </Row>
        </Col>
      </Row>
      <UsersTable
        loading={isLoading}
        columns={columns}
        dataSource={users}
        // onRow={handleOnRow}
        onChange={(pagination, filters, sorter) => {
          handleOnAggregationTableChange(
            { pagination, filters, sorter },
            setQuery
          );
        }}
        scroll={{
          x: 1300,
        }}
        pagination={{ total, current: query.currentPage }}
        rowKey={(record) => record._id}
        showSorterTooltip={false}
      />

      {PDFModal}
    </div>
  );
};

export default withAppContext(FinportGoals);
